import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Encapsulates @mention autocomplete state and handlers for chat inputs.
 *
 * @param {object} options
 * @param {string}   options.text         - current input value (from parent state)
 * @param {function} options.setText      - state setter for the input value
 * @param {array}    options.participants - array of { member_id, name, isEveryone? }
 * @param {object}   options.inputRef    - React ref attached to the <input> element
 *
 * @returns {{
 *   mentionOpen: boolean,
 *   setMentionOpen: function,
 *   mentionIdx: number,
 *   filteredParticipants: array,
 *   selectMention: function,
 *   onInputChange: function,   // use as onChange handler (or call from your own)
 *   onKeyDown: function,       // use as onKeyDown handler (or call from your own)
 *   onInputBlur: function,     // use as onBlur handler (or call from your own)
 * }}
 */
export function useMentionAutocomplete({ text, setText, participants, inputRef }) {
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIdx, setMentionIdx] = useState(0);

  // Reset highlighted index whenever the query changes
  useEffect(() => { setMentionIdx(0); }, [mentionQuery]);

  const filteredParticipants = useMemo(() => {
    if (!mentionQuery) return participants;
    return participants.filter(p =>
      p.name.toLowerCase().startsWith(mentionQuery.toLowerCase())
    );
  }, [participants, mentionQuery]);

  /** Insert the selected @mention into the input at the cursor position */
  const selectMention = useCallback((name) => {
    const pos = inputRef.current?.selectionStart ?? text.length;
    const textBefore = text.slice(0, pos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = text.slice(0, atIdx) + `@${name} ` + text.slice(pos);
    setText(newText);
    setMentionOpen(false);
    setMentionQuery('');
    setTimeout(() => {
      if (inputRef.current) {
        const newPos = atIdx + name.length + 2;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }, [text, setText, inputRef]);

  /**
   * onChange handler: updates text state + detects @ mentions.
   * Call this directly as onChange, or call it inside your own onChange handler
   * before running component-specific side effects (e.g. typing indicators).
   */
  const onInputChange = useCallback((e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setText(val);
    const textBefore = val.slice(0, pos);
    const atMatch = textBefore.match(/@([\w][\w ]*)?$/);
    if (atMatch) {
      setMentionQuery(atMatch[1] || '');
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
      setMentionQuery('');
    }
  }, [setText]);

  /** onKeyDown handler: arrow navigation, Enter/Tab to select, Esc to close */
  const onKeyDown = useCallback((e) => {
    if (!mentionOpen || filteredParticipants.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIdx(i => Math.min(i + 1, filteredParticipants.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filteredParticipants[mentionIdx]) {
        e.preventDefault();
        selectMention(filteredParticipants[mentionIdx].name);
      }
    } else if (e.key === 'Escape') {
      setMentionOpen(false);
      setMentionQuery('');
    }
  }, [mentionOpen, filteredParticipants, mentionIdx, selectMention]);

  /** onBlur handler: close dropdown with a small delay (allows click-to-select) */
  const onInputBlur = useCallback(() => {
    setTimeout(() => setMentionOpen(false), 150);
  }, []);

  return {
    mentionOpen,
    setMentionOpen,
    mentionIdx,
    filteredParticipants,
    selectMention,
    onInputChange,
    onKeyDown,
    onInputBlur,
  };
}
